import { Volume2, VolumeX } from 'lucide-react';
import { useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNow } from '@despachador/ui';
import { useSession } from '../../lib/session';
import { elapsed, shortName, usePanicAlerts, type PanicAlert } from '../../lib/panic';

/** Lines the strip shows before «y N más». */
const MAX_LINES = 3;

/**
 * The silent alerts above every screen of the panel (PNC-001): one red line per open alert, with
 * «La atiendo» right there. On a phone each line becomes a block with the trip and the reminder not
 * to call the driver. A cancelled alert stays 30 s saying so, then leaves.
 */
export function PanicStrip() {
  const { open, cancelled, sounding, audioBlocked, acknowledge, enabled } = usePanicAlerts();
  const { can } = useSession();
  const navigate = useNavigate();
  const now = useNow(1000);
  const visible = enabled && (open.length > 0 || cancelled.length > 0);
  // the strip runs behind the floating menu: the menu's column needs to know where the strip ends
  const strip = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = strip.current;
    if (!visible || !el) return;
    const root = document.documentElement;
    const measure = new ResizeObserver(() => root.style.setProperty('--panic-strip-h', `${el.offsetHeight}px`));
    measure.observe(el);
    return () => {
      measure.disconnect();
      root.style.removeProperty('--panic-strip-h');
    };
  }, [visible]);
  if (!visible) return null;
  const shown = open.slice(0, MAX_LINES);
  const show = (a: PanicAlert) => navigate(`/en-vivo?alerta=${a.id}`);
  return (
    <div ref={strip} className="panic-strip" role="alert">
      {shown.map((a, i) => (
        <div key={a.id} className={`ps-line ${a.status === 'ACTIVE' ? 'unattended' : 'attended'}`}>
          <span className="ps-ping" aria-hidden />
          <span className="ps-what">
            <b className="ps-caps">Alerta silenciosa</b>
            <span className="ps-status-phone"> · {a.status === 'ACTIVE' ? 'sin atender' : `atiende ${shortName(a.acknowledged?.name)}`}</span>
          </span>
          <span className="ps-who">
            {a.driver.name} · <span className="mono">{a.vehicle?.plate ?? 'sin vehículo'}</span>
            {a.service && (
              <>
                {' '}
                · en servicio <span className="mono">{a.service.code}</span>
              </>
            )}
          </span>
          <span className="ps-age mono">hace {elapsed(a.startedAt, now)}</span>
          <span className="ps-status">{a.status === 'ACTIVE' ? 'Sin atender' : `Atiende ${shortName(a.acknowledged?.name)}`}</span>
          <span className="ps-more">
            {a.service?.destination ? `→ ${a.service.destination}` : 'Sin servicio en curso'}
            {a.position && ` · ubicación hace ${elapsed(a.position.at, now)}`}
            <b className="ps-warn">No llames al conductor.</b>
          </span>
          <span className="ps-actions">
            {i === 0 && sounding && (
              <span className="ps-sound">
                {audioBlocked ? (
                  <>
                    <VolumeX size={16} aria-hidden /> Haz clic en la página para activar el sonido
                  </>
                ) : (
                  <>
                    <Volume2 size={16} aria-hidden /> Sonando
                  </>
                )}
              </span>
            )}
            {a.status === 'ACTIVE' && can('panic.manage') && (
              <button type="button" className="btn sm ps-btn" onClick={() => void acknowledge(a).then((ok) => void (ok && show(a)))}>
                La atiendo
              </button>
            )}
            <button type="button" className="btn sm ps-btn ghost-light" onClick={() => show(a)}>
              Ver en el mapa
            </button>
          </span>
        </div>
      ))}
      {open.length > MAX_LINES && (
        <button type="button" className="ps-rest" onClick={() => navigate('/alertas-panico?estado=activas')}>
          y {open.length - MAX_LINES} más
        </button>
      )}
      {cancelled.map((a) => (
        <div key={a.id} className="ps-line ended">
          <span className="ps-what">
            <b className="ps-caps">Cancelada por el conductor</b>
          </span>
          <span className="ps-who">
            {a.driver.name} · <span className="mono">{a.vehicle?.plate ?? 'sin vehículo'}</span>
          </span>
        </div>
      ))}
    </div>
  );
}
